import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BoardQuery,
  BoardResponse,
  CloseOpportunityInput,
  MoveStageInput,
  Opportunity,
  ReopenOpportunityInput,
} from "@salesboard/contracts";
import type { QueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { ApiClientError, api, toQueryString } from "@/lib/api";
import { reportMutationError, reportMutationSuccess } from "@/lib/mutations";

/**
 * The board query key is list-shaped — `["opportunities", "board", …]` — deliberately.
 * The SSE client matches on the `opportunities` root, so live invalidation works with no
 * change to it, and because it is not a *detail* key, even the acting user's own event
 * correctly refreshes the board.
 */
/**
 * Report a refused move, and — when the refusal was a stale version — re-read the board.
 *
 * There is no refresh affordance in this product, so "someone else changed this" has to
 * repair itself: the invalidation is what turns a dead end into a retry that can succeed.
 */
function reportMoveError(queryClient: QueryClient, error: unknown): void {
  if (error instanceof ApiClientError && error.isStaleVersion) {
    void queryClient.invalidateQueries({ queryKey: ["opportunities"] });
  }
  reportMutationError(error);
}

export function boardQueryKey(params: BoardQuery) {
  return ["opportunities", "board", params] as const;
}

export function useBoard(params: BoardQuery) {
  return useQuery({
    queryKey: boardQueryKey(params),
    // The whole query, not a hand-copied field list. An enumerated list silently dropped
    // `q` when board search arrived: the key included the term so the board *refetched*,
    // and refetched unfiltered — the chip and the URL asserting a filter the request never
    // carried. `BoardQuery` is exactly the endpoint's vocabulary, so spreading it means a
    // new filter cannot be added to the contract and forgotten here.
    queryFn: () => api.get<BoardResponse>(`/opportunities/board${toQueryString({ ...params })}`),
  });
}

/**
 * The three lifecycle mutations.
 *
 * None of them patches the cache. The pending treatment is local presentation state (see
 * `useMoveFlow`); the cache only ever holds committed server truth, so no count or total on
 * screen is a guess.
 *
 * Each `onSuccess` **returns** its invalidation, which makes `mutateAsync` wait for the
 * refetch. That is load-bearing: the caller releases the card's pending placement when
 * `mutateAsync` resolves, and resolving before the board has re-read would drop the card
 * back into its old cell for a frame — a spring-back produced by the client, on a move the
 * server had already accepted.
 *
 * A refused write invalidates too. A `STALE_VERSION` means this client is looking at
 * something older than the server, and the product offers no reload button by design, so
 * the refusal itself has to be what restores freshness — otherwise a user whose event was
 * missed during a short disconnect can be refused forever with no way forward.
 */
export function useMoveStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: MoveStageInput & { id: string }) =>
      api.post<Opportunity>(`/opportunities/${id}/stage`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["opportunities"] }),
    onError: (error) => reportMoveError(queryClient, error),
  });
}

export function useCloseOpportunity() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ id, ...input }: CloseOpportunityInput & { id: string }) =>
      api.post<Opportunity>(`/opportunities/${id}/close`, input),
    onSuccess: async (opportunity) => {
      await queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      reportMutationSuccess(opportunity.status === "won" ? t("toast.closedWon") : t("toast.closedLost"));
    },
    onError: (error) => reportMoveError(queryClient, error),
  });
}

export function useReopenOpportunity() {
  const queryClient = useQueryClient();
  const t = useT();
  return useMutation({
    mutationFn: ({ id, ...input }: ReopenOpportunityInput & { id: string }) =>
      api.post<Opportunity>(`/opportunities/${id}/reopen`, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["opportunities"] });
      reportMutationSuccess(t("toast.reopened"));
    },
    onError: (error) => reportMoveError(queryClient, error),
  });
}

/** A pure quarter move is the four-field edit — and must not reset the stage timer. */
export function useMoveQuarter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, expectedCloseDate, version }: { id: string; expectedCloseDate: string; version: number }) =>
      api.patch<Opportunity>(`/opportunities/${id}`, { expectedCloseDate, version }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["opportunities"] }),
    onError: (error) => reportMoveError(queryClient, error),
  });
}
